import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { generatePath, Link } from "react-router-dom";
import {
    IOccupiedSlot,
    IPreviewStudentRegularScheduleResponse,
    ISlotPattern,
    IStudentRegularSchedule,
    scheduleService,
    TScheduleAction,
    TScheduleConflictReason,
} from "../../../../entities/schedule";
import { ILocalDate } from "../../../../entities/calendar";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useMyActions } from "../../../../entities/my";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { LoaderSpinner } from "../../../../shared/ui/spinner";
import { StudentRegularScheduleDelete } from "../../../../features/studentRegularScheduleDelete";
import { STUDENT_ROUTE } from "../../../../app/router/routes";
import { MyInput } from "../../../../shared/ui/input";
import { ToggleSwitch } from "../../../../shared/ui/toggleSwitch";
import {
    addDaysToLocalDate,
    formatDateTime,
    formatLocalDate,
    getDateInputValue,
    getLocalDateFromInput,
    getSlotPatternFromDateTimeSlot,
    getTodayLocalDate,
} from "../../lib/scheduleDate";
import { getSlotTimeLabel, getWeekdayLabel } from "../../lib/slotPattern";
import { ScheduleWeekGrid } from "../weekGrid/ScheduleWeekGrid";
import classes from '../scheduleCalendar.module.scss'

interface IProps {
    studentId: string;
    userId: string;
    tgAccountId: string;
    tagIds: string[];
    title?: string;
    subtitle?: string;
    hideTitle?: boolean;
}

const CONFLICT_REASON_LABELS: Record<TScheduleConflictReason, string> = {
    SCHEDULE_CONFLICT_REASON_UNSPECIFIED: 'Конфликт',
    SCHEDULE_CONFLICT_REASON_TUTOR_NOT_AVAILABLE: 'Исполнитель недоступен',
    SCHEDULE_CONFLICT_REASON_TUTOR_BUSY: 'Исполнитель занят',
    SCHEDULE_CONFLICT_REASON_STUDENT_BUSY: 'Студент занят',
}

export const StudentRegularScheduleCalendar: FC<IProps> = ({
    studentId,
    userId,
    tgAccountId,
    tagIds,
    title = 'Регулярный график студента',
    subtitle = 'Выберите повторяющиеся слоты. Перед сохранением можно посмотреть будущие занятия и конфликты.',
    hideTitle = false,
}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [schedule, setSchedule] = useState<IStudentRegularSchedule | null>(null)
    const [availableActions, setAvailableActions] = useState<TScheduleAction[]>([])
    const [availabilitySlots, setAvailabilitySlots] = useState<ISlotPattern[]>([])
    const [occupiedSlots, setOccupiedSlots] = useState<IOccupiedSlot[]>([])
    const [weeklySlots, setWeeklySlots] = useState<ISlotPattern[]>([])
    const [startsOn, setStartsOn] = useState<ILocalDate>(getTodayLocalDate())
    const [endsOn, setEndsOn] = useState<ILocalDate | undefined>()
    const [previewUntil, setPreviewUntil] = useState<ILocalDate>(addDaysToLocalDate(getTodayLocalDate(), 30))
    const [materializeLessons, setMaterializeLessons] = useState(true)
    const [preview, setPreview] = useState<IPreviewStudentRegularScheduleResponse | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isPreviewLoading, setIsPreviewLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
    const [error, setError] = useState('')

    const fallbackTagId = tagIds[0] || ''
    const effectiveTagId = schedule?.tagId && tagIds.includes(schedule.tagId)
        ? schedule.tagId
        : fallbackTagId
    const hasEffectiveTag = Boolean(effectiveTagId)

    const scheduleActions = schedule?.availableActions.length
        ? schedule.availableActions
        : availableActions
    const hasPersistedSchedule = Boolean(schedule?.id && schedule.id !== '0')
    const canUpdate = hasEffectiveTag && scheduleActions.includes('SCHEDULE_ACTION_UPDATE')
    const canPreview = hasEffectiveTag && scheduleActions.includes('SCHEDULE_ACTION_PREVIEW_GENERATED_LESSONS')
    const canDelete = hasPersistedSchedule && scheduleActions.includes('SCHEDULE_ACTION_DELETE')
    const previewConflictSlots = useMemo(() => {
        if(!preview?.conflicts.length) return []

        return preview.conflicts
            .map(conflict => getSlotPatternFromDateTimeSlot(conflict.startsAt, conflict.durationSlots))
            .filter((slot): slot is ISlotPattern => Boolean(slot))
    }, [preview])

    useEffect(() => {
        let isActual = true

        const getData = async() => {
            setIsLoading(true)
            setError('')
            setPreview(null)

            try{
                const [availabilityResponse, scheduleResponse] = await Promise.all([
                    scheduleService.getTutorAvailability({userId}),
                    scheduleService.getStudentRegularSchedule(studentId),
                ])

                if(!isActual) return

                const currentSchedule = scheduleResponse.schedule ?? null
                const initialStartsOn = currentSchedule?.startsOn ?? getTodayLocalDate()

                setAvailabilitySlots(availabilityResponse.availability.weeklySlots)
                setOccupiedSlots((availabilityResponse.availability.occupiedSlots ?? []).filter(slot => {
                    if(!slot.student?.id) return true

                    return String(slot.student.id) !== String(studentId)
                }))
                setSchedule(currentSchedule)
                setAvailableActions(scheduleResponse.availableActions ?? [])
                setWeeklySlots(currentSchedule?.weeklySlots ?? [])
                setStartsOn(initialStartsOn)
                setEndsOn(currentSchedule?.endsOn)
                setPreviewUntil(addDaysToLocalDate(initialStartsOn, 30))
            }
            catch(error){
                console.log(error)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setError('Ошибка при получении регулярного графика студента')
                    setGlobalMessage({message: 'Ошибка при получении регулярного графика студента', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [studentId, userId])

    const previewSchedule = useCallback(async() => {
        if(!canPreview || !weeklySlots.length) return

        setIsPreviewLoading(true)
        setError('')

        try{
            const response = await scheduleService.previewStudentRegularSchedule({
                studentId,
                userId,
                tgAccountId,
                tagId: effectiveTagId,
                startsOn,
                weeklySlots,
                previewUntil,
            })

            setPreview(response)
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError('Ошибка при предварительном расчёте графика')
                setGlobalMessage({message: 'Ошибка при предварительном расчёте графика', type: 'error'})
            }
        }
        finally{
            setIsPreviewLoading(false)
        }
    }, [
        canPreview,
        weeklySlots,
        studentId,
        userId,
        tgAccountId,
        effectiveTagId,
        startsOn,
        previewUntil,
        setGlobalMessage,
        setIsAuth,
    ])


    const startPreviewSchedule = () => {
        previewSchedule()
    }

    const saveSchedule = async() => {
        if(!canUpdate || !weeklySlots.length) return

        setIsSaving(true)
        setError('')

        try{
            const response = await scheduleService.updateStudentRegularSchedule({
                studentId,
                userId,
                tgAccountId,
                tagId: effectiveTagId,
                startsOn,
                endsOn,
                weeklySlots,
                materializeLessons,
            })

            setSchedule(response.schedule)
            setAvailableActions(response.schedule.availableActions)
            setWeeklySlots(response.schedule.weeklySlots)
            setGlobalMessage({message: 'График сохранён', type: 'ok'})
        }
        catch(error){
            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                if(error.message.toLowerCase().includes('конфликт')){
                    previewSchedule()
                }
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setError('Ошибка при сохранении регулярного графика')
                setGlobalMessage({message: 'Ошибка при сохранении регулярного графика', type: 'error'})
            }
        }
        finally{
            setIsSaving(false)
        }
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    {!hideTitle && <h2 className={classes.title}>{title}</h2>}
                    <p className={classes.subtitle}>{subtitle}</p>
                    <p className={classes.subtitle}>Все слоты указываются по МСК.</p>
                </section>
                <ul className={classes.legend} aria-label="Обозначения графика">
                    <li className={`${classes.legendItem} ${classes.disabled}`}>Исполнитель недоступен</li>
                    <li className={`${classes.legendItem} ${classes.available}`}>Исполнитель доступен</li>
                    <li className={`${classes.legendItem} ${classes.occupied}`}>Регулярное занятие</li>
                    <li className={`${classes.legendItem} ${classes.conflict}`}>Конфликт</li>
                </ul>
            </section>

            {error && <p className={classes.error}>{error}</p>}
            {!hasEffectiveTag && !isLoading && (
                <p className={classes.error}>Для редактирования регулярного графика у студента должен быть назначен хотя бы один тег.</p>
            )}

            {isLoading ?
                <section className={classes.message}><LoaderSpinner /></section>
                :
                <>
                    <section className={classes.controls}>
                        <MyInput
                            type="date"
                            title="Дата начала"
                            value={getDateInputValue(startsOn)}
                            isLoading={!canUpdate}
                            setError={setError}
                            setValue={value => {
                                setStartsOn(getLocalDateFromInput(value))
                                setPreview(null)
                            }}
                        />
                        <MyInput
                            type="date"
                            title="Дата окончания"
                            value={getDateInputValue(endsOn)}
                            isLoading={!canUpdate}
                            setError={setError}
                            setValue={value => setEndsOn(value ? getLocalDateFromInput(value) : undefined)}
                        />
                        <MyInput
                            type="date"
                            title="Предпросмотр до"
                            value={getDateInputValue(previewUntil)}
                            isLoading={!canPreview}
                            setError={setError}
                            setValue={value => {
                                setPreviewUntil(getLocalDateFromInput(value))
                                setPreview(null)
                            }}
                        />
                        <section className={classes.checkbox}>
                            <ToggleSwitch
                                checked={materializeLessons}
                                onSelected={canUpdate ? setMaterializeLessons : () => {}}
                                label="Создать будущие занятия"
                            />
                        </section>
                    </section>

                    <ScheduleWeekGrid
                        variant="regular"
                        value={weeklySlots}
                        onChange={value => {
                            setWeeklySlots(value)
                            setPreview(null)
                        }}
                        availabilitySlots={availabilitySlots}
                        conflictSlots={previewConflictSlots}
                        occupiedSlots={occupiedSlots}
                        editable={canUpdate}
                        allowUnavailableSelection
                        disabledText="Исполнитель недоступен"
                    />

                    {!canUpdate &&
                        <p className={classes.message}>Регулярный график доступен только для просмотра.</p>
                    }

                    <section className={classes.actions}>
                        {canDelete &&
                            <button
                                type="button"
                                onClick={() => setIsDeleteConfirmOpen(true)}
                                disabled={isSaving}
                            >
                                Удалить график
                            </button>
                        }
                        {canPreview &&
                            <button type="button" onClick={startPreviewSchedule} disabled={isPreviewLoading || !weeklySlots.length}>
                                {isPreviewLoading ? 'Считаем...' : 'Предпросмотр'}
                            </button>
                        }
                        {canUpdate &&
                            <button type="button" onClick={saveSchedule} disabled={isSaving || !weeklySlots.length}>
                                {isSaving ? 'Сохраняем...' : 'Сохранить'}
                            </button>
                        }
                    </section>

                    {preview &&
                        <section className={classes.preview}>
                            <section className={classes.previewBlock}>
                                <h3>Будущие занятия</h3>
                                {preview.lessons.length ?
                                    <ul>
                                        {preview.lessons.map((lesson, index) => (
                                            <li key={`${lesson.startsAt}-${index}`}>
                                                {formatDateTime(lesson.startsAt)} · {lesson.durationSlots * 30} мин.
                                            </li>
                                        ))}
                                    </ul>
                                    :
                                    <p className={classes.subtitle}>По выбранному графику занятия не найдены.</p>
                                }
                            </section>
                            <section className={classes.previewBlock}>
                                <h3>Конфликты</h3>
                                {preview.conflicts.length ?
                                    <ul>
                                        {preview.conflicts.map((conflict, index) => {
                                            const pattern = getSlotPatternFromDateTimeSlot(conflict.startsAt, conflict.durationSlots)

                                            return (
                                                <li key={`${conflict.startsAt}-${index}`}>
                                                    <span>
                                                        {CONFLICT_REASON_LABELS[conflict.reason]} · {formatDateTime(conflict.startsAt)}
                                                        {pattern && ` · ${getWeekdayLabel(pattern.weekday)} ${getSlotTimeLabel(pattern.startSlot)}`}
                                                        {conflict.description && ` · ${conflict.description}`}
                                                    </span>
                                                    {Boolean(conflict.conflictingLessons?.length) && (
                                                        <section className={classes.conflictLinks}>
                                                            {conflict.conflictingLessons.map(lesson => (
                                                                <Link
                                                                    key={`${lesson.lessonId}-${lesson.studentId}`}
                                                                    to={generatePath(STUDENT_ROUTE.path, {id: lesson.studentId})}
                                                                >
                                                                    {lesson.studentName || `Ученик ${lesson.studentId}`}
                                                                </Link>
                                                            ))}
                                                        </section>
                                                    )}
                                                </li>
                                            )
                                        })}
                                    </ul>
                                    :
                                    <p className={classes.subtitle}>Конфликтов нет.</p>
                                }
                            </section>
                        </section>
                    }

                    {hasPersistedSchedule && schedule?.startsOn &&
                        <p className={classes.message}>
                            Текущий график действует с {formatLocalDate(schedule.startsOn)}{schedule.endsOn ? ` по ${formatLocalDate(schedule.endsOn)}` : ''}.
                        </p>
                    }

                    <StudentRegularScheduleDelete
                        open={isDeleteConfirmOpen}
                        setOpen={setIsDeleteConfirmOpen}
                        studentId={studentId}
                        onSuccess={() => {
                            setSchedule(null)
                            setWeeklySlots([])
                            setPreview(null)
                        }}
                    />
                </>
            }
        </section>
    )
}
